import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Briefcase, CalendarClock, Check, LogOut, Receipt, Users } from "lucide-react";
import { CardFrame, fadeUp } from "./IntakeCards";
import { CountUp } from "@/components/clientDemo/intro/IntroStats";

const P = "exchange-career-card";
const CERTS = ["Work-Ready cleared", "Drug screen passed", "Forklift certified", "OSHA-10 safety"];

export function PreCertCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="precert" n={1} title="Workforce Pre-Certification" Icon={BadgeCheck} active={active}>
      <div key={run} className="relative">
        <div className="text-xs text-slate-400 mb-2">Employer-ready portfolio · <span className="text-white">Marcus T.</span></div>
        <ul className="grid grid-cols-2 gap-1.5 text-xs">
          {CERTS.map((c, i) => (
            <motion.li key={c} {...fadeUp(0.15 + i * 0.25)} className="flex items-center gap-1.5 rounded-md bg-white/[0.03] border border-white/5 px-2 py-1.5 text-slate-200">
              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />{c}
            </motion.li>
          ))}
        </ul>
        <motion.div data-testid="exchange-career-stamp" initial={{ scale: 2.2, opacity: 0, rotate: -18 }} animate={{ scale: 1, opacity: 1, rotate: -8 }}
          transition={{ delay: 1.4, type: "spring", stiffness: 260, damping: 14 }}
          className="mt-3 inline-block px-3 py-1 rounded-md border-2 border-[#D4AF37] text-[#F4D88A] font-mono text-xs font-bold tracking-[0.2em] uppercase shadow-[0_0_20px_rgba(212,175,55,0.35)]">
          Pre-Certified
        </motion.div>
      </div>
    </CardFrame>
  );
}

const TRACKS = ["Hospitality", "Logistics", "Skilled Trades", "Healthcare"];

export function PlacementCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="placement" n={2} title="Career Destinations & Placement" Icon={Briefcase} active={active}>
      <div key={run}>
        <div className="grid grid-cols-4 gap-1.5">
          {TRACKS.map((t, i) => (
            <motion.div key={t} initial={{ opacity: 0.3 }} animate={{ opacity: 1, borderColor: t === "Logistics" ? "rgba(212,175,55,0.8)" : "rgba(255,255,255,0.08)" }}
              transition={{ delay: 0.15 + i * 0.2 }}
              className={`rounded-md border px-1.5 py-2 text-center text-[10px] ${t === "Logistics" ? "bg-[#D4AF37]/15 text-[#FFF4CC]" : "text-slate-400"}`}>{t}</motion.div>
          ))}
        </div>
        <motion.div {...fadeUp(1.1)} data-testid="exchange-career-placed" className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-xs">
          <Check className="w-4 h-4 text-emerald-300 shrink-0" />
          <span className="text-white">Placed · Warehouse Associate · Bridge Logistics</span>
        </motion.div>
        <motion.div {...fadeUp(1.6)} className="mt-2 flex items-baseline gap-2">
          <span className="font-mono text-xl font-bold text-[#F4D88A]">$45K–$55K</span>
          <span className="text-[10px] uppercase tracking-wider text-slate-400">Career track wages</span>
        </motion.div>
      </div>
    </CardFrame>
  );
}

const CREW = [["Marcus T.", "On shift", "On time"], ["Dana R.", "On shift", "On time"], ["Luis M.", "Scheduled 2 PM", "—"]];

export function RosterCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="roster" n={3} title="Employer Roster & Live Pay-Period Reporting" Icon={Users} active={active}>
      <div key={run} className="space-y-1.5 text-xs">
        {CREW.map(([n, s, c], i) => (
          <motion.div key={n} {...fadeUp(0.1 + i * 0.2)} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center rounded-md bg-white/[0.03] border border-white/5 px-2.5 py-1.5">
            <span className="text-white">{n}</span>
            <span className={s === "On shift" ? "text-emerald-300" : "text-slate-400"}>{s}</span>
            <span className="font-mono text-[10px] text-slate-500 w-12 text-right">{c}</span>
          </motion.div>
        ))}
        <motion.div {...fadeUp(0.9)} className="flex items-center gap-2 pt-1 text-[#F4D88A]">
          <Receipt className="w-3.5 h-3.5" /> Receipt captured by smartphone · 8.0 hrs
        </motion.div>
        <motion.div {...fadeUp(1.2)} data-testid="exchange-career-hours" className="flex items-baseline gap-2">
          <span className="font-mono text-xl font-bold text-[#F4D88A] tabular-nums"><CountUp to={312} duration={1400} /></span>
          <span className="text-[10px] uppercase tracking-wider text-slate-400">billable hours · this pay period</span>
        </motion.div>
      </div>
    </CardFrame>
  );
}

const SNAPSHOT = [["Gross earnings", 4860], ["Rent paid", 1200], ["Savings bucket", 1850], ["Net cash delivered", 1310]];

export function EarningsCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="earnings" n={4} title="Work Schedule & Earnings Snapshot" Icon={CalendarClock} active={active}>
      <div key={run}>
        <motion.div {...fadeUp(0.1)} className="flex items-center gap-2 rounded-md bg-white/[0.03] border border-white/5 px-2.5 py-1.5 text-xs">
          <span className="text-slate-400">Today</span><span className="text-white">Work Shift · 7 AM – 3 PM</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[#F4D88A]"><LogOut className="w-3 h-3" /> Log My Departure</span>
        </motion.div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {SNAPSHOT.map(([label, to], i) => (
            <motion.div key={label} {...fadeUp(0.4 + i * 0.15)} data-testid={`exchange-career-snapshot-${i}`} className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
              <div className="font-mono text-lg font-bold text-[#F4D88A] tabular-nums">$<CountUp to={to} duration={1500} /></div>
              <div className="text-[10px] text-slate-400">{label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </CardFrame>
  );
}
